import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Box } from '@mui/material'
import {
  OnboardingInnerWrapper,
  OnboardingItemListChip,
  OnboardingWrapper,
} from '../../../eater/onboardPages/styled/OnboardPreferenceOptions.styled'
import { SANS_FONT } from '../../../../theme'
import { OnboardContentNicheMethods } from '../../../../types/contentCreatorTypes'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import {
  selectOnboardContentNicheDetail,
  setOnboardContentNicheDetail,
} from '../../../../store/influencer/contentCreator'
import { useAppSelector } from '../../../../store'

interface ContentNicheStepProps {
  editMode: boolean
  items: string[]
  setIsValidData?: (isValid: boolean) => void // Optional validation callback
}

const ContentNicheStep = forwardRef<
  OnboardContentNicheMethods,
  ContentNicheStepProps
>(({ editMode, items, setIsValidData }, ref) => {
  const contentNiche = useAppSelector(selectOnboardContentNicheDetail)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const { dispatch } = useAppUtilities()

  useEffect(() => {
    if (contentNiche) {
      setSelectedItems(new Set(contentNiche))
    }
  }, [contentNiche])

  // Validation: require at least 1 niche selected
  useEffect(() => {
    if (!setIsValidData) return
    
    const isValid = selectedItems.size > 0
    setIsValidData(isValid)
  }, [selectedItems, setIsValidData])

  const handleItemToggle = (item: string): void => {
    if (!editMode) return
    setSelectedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(item)) {
        newSet.delete(item)
      } else {
        newSet.add(item)
      }
      return newSet
    })
  }

  const getPayload = async (): Promise<string[]> => {
    const payload = Array.from(selectedItems)
    dispatch(setOnboardContentNicheDetail(payload))
    return payload
  }

  const resetForm = (): void => {
    setSelectedItems(new Set(contentNiche))
  }

  useImperativeHandle(ref, () => ({ getPayload, resetForm }))

  return (
    <OnboardingWrapper justifyContent={'flex-start'} pt={1}>
      {items
        .filter((item) => editMode || selectedItems.has(item))
        .map((item, index) => (
          <OnboardingInnerWrapper key={index}>
            <Box component="div" sx={{ alignItems: 'start' }}>
              <OnboardingItemListChip
                sx={{
                  paddingX: '8px',
                  color:
                    selectedItems.has(item) || !editMode
                      ? 'success.dark'
                      : 'grey.800',
                  borderColor:
                    selectedItems.has(item) || !editMode
                      ? 'success.dark'
                      : 'grey.300',
                  backgroundColor:
                    selectedItems.has(item) || !editMode
                      ? 'success.50'
                      : 'background.paper',
                  fontSize: '14px',
                  cursor: editMode ? 'pointer' : 'default',
                }}
                onClick={() => handleItemToggle(item)}
                label={item}
                style={{
                  fontFamily: SANS_FONT,
                  textTransform: 'capitalize',
                }}
              />
            </Box>
          </OnboardingInnerWrapper>
        ))}
    </OnboardingWrapper>
  )
})

ContentNicheStep.displayName = 'ContentNicheStep'

export default ContentNicheStep
